import {
  Input,
  inject,
  Output,
  OnChanges,
  Component,
  DestroyRef,
  EventEmitter,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { RoleComponent, RoleModule } from '../../../admin.models';

import { BehaviorSubject, tap } from 'rxjs';
import { SearchInputComponent } from '../../../../shared/components/search-input/search-input.component';
import { FormsModule } from '@angular/forms';
import { SuiCheckboxComponent } from '@sui/checkbox';
import { TranslatePipe } from '@ngx-translate/core';
import { HighlightPipe } from '../../../../shared/pipes/highlight.pipe';

@Component({
  selector: 'app-role-features',
  templateUrl: './role-features.component.html',
  styleUrl: './role-features.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SearchInputComponent,
    FormsModule,
    SuiCheckboxComponent,
    TranslatePipe,
    HighlightPipe,
  ],
})
export class RoleFeaturesComponent implements OnChanges {
  @Input() roleModules: RoleModule[];
  @Input() searchString$: BehaviorSubject<string>;
  @Output() selectedComponentsChange = new EventEmitter<RoleComponent[]>();

  localSearchString: string = '';
  globalSearchString: string = '';
  selectedComponents: RoleComponent[] = [];
  showSelectedOnly: boolean = false;

  private destroyRef = inject(DestroyRef);

  get activeSearchString(): string {
    return this.globalSearchString || this.localSearchString;
  }

  get isLocalSearchDisabled(): boolean {
    return this.globalSearchString?.length > 0;
  }

  ngOnInit(): void {
    // * Subscribe to parent global search for cross-tab search integration
    if (this.searchString$) {
      this.searchString$
        .pipe(
          tap((val) => {
            this.globalSearchString = val;

            // When global search becomes active, clear local search
            if (val?.length > 0) {
              this.localSearchString = '';
            }
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }

    // * Initialize selected components if modules are available
    this.initializeSelectedComponents();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // * When async data arrives, re-initialize selected components
    if (changes['roleModules'] && this.roleModules) {
      this.initializeSelectedComponents();
    }
  }

  ngAfterViewInit(): void {
    this.setPermissionsHeight();
  }

  /**
   * Initialize selected components from roleModules
   * Called both in ngOnInit (sync) and ngOnChanges (async)
   */
  private initializeSelectedComponents(): void {
    if (!this.roleModules) {
      return;
    }

    // * Clear existing selections to avoid duplicates
    this.selectedComponents = [];

    this.roleModules.forEach((module) => {
      // * Check root component
      if (module.root_component?.selected) {
        module.root_component.is_root_component = true;
        this.selectedComponents.push(module.root_component);
      }

      // * Check child components
      module.components?.forEach((component) => {
        if (component.selected) {
          this.selectedComponents.push(component);
        }
      });
    });
  }

  // * Handles the change event of a component checkbox.
  // * Handles the change event of a component checkbox.
  onComponentChange(component: RoleComponent, selected: boolean) {
    component.selected = selected;

    // 1. Better way to find parent if module_id is missing on component
    // (Optional improvement: ensures you find the module even if component.module_id is undefined)
    const parentModule = this.roleModules.find(
      (module) =>
        module.id === component.module_id ||
        module.components?.some((c) => c.id === component.id),
    );

    if (component.is_root_component) {
      // * Root component logic
      if (selected) {
        this.addToSelectedComponents(component, true);
      } else {
        this.removeFromSelectedComponents(component);
        if (parentModule?.components) {
          parentModule.components.forEach((childComponent) => {
            childComponent.selected = false;
            this.removeFromSelectedComponents(childComponent);
          });
        }
      }
    } else {
      // * Child component logic
      if (selected) {
        this.addToSelectedComponents(component);

        // FIX: Check if Root exists and is NOT selected yet.
        // We don't care about the count of children, just the state of the root.
        if (
          parentModule?.root_component &&
          !parentModule.root_component.selected
        ) {
          parentModule.root_component.selected = true;
          this.addToSelectedComponents(parentModule.root_component, true);
        }
      } else {
        // * Child deselected: just remove this child
        this.removeFromSelectedComponents(component);
      }
    }

    this.selectedComponentsChange.emit(this.selectedComponents);
  }

  toggleModuleSelection(selected: boolean, roleModule: RoleModule) {
    // * toggle Root component
    if (roleModule.root_component) {
      roleModule.root_component.selected = selected;
      if (selected) {
        this.addToSelectedComponents(roleModule.root_component, true);
      } else {
        this.removeFromSelectedComponents(roleModule.root_component);
      }
    }

    // * toggle all Child components
    if (roleModule.components) {
      roleModule.components.forEach((component) => {
        component.selected = selected;
        if (selected) {
          this.addToSelectedComponents(component);
        } else {
          this.removeFromSelectedComponents(component);
        }
      });
    }

    this.selectedComponentsChange.emit(this.selectedComponents);
  }

  isModuleFullySelected(roleModule: RoleModule): boolean {
    let totalComponents = 0;
    let selectedComponents = 0;

    // * check Root Component
    if (roleModule.root_component) {
      totalComponents++;
      if (roleModule.root_component.selected) {
        selectedComponents++;
      }
    }

    // * check Child Components
    if (roleModule.components) {
      totalComponents += roleModule.components.length;
      selectedComponents += roleModule.components.filter(
        (c) => c.selected,
      ).length;
    }

    return totalComponents > 0 && selectedComponents === totalComponents;
  }

  getTotalComponentsCount(): number {
    // * Handle async loading: return 0 if modules not loaded yet
    if (!this.roleModules) {
      return 0;
    }

    let total = 0;

    this.roleModules.forEach((module) => {
      if (module.root_component) {
        total++;
      }

      if (module.components) {
        total += module.components.length;
      }
    });

    return total;
  }

  getSelectedComponentsCount(): number {
    return this.selectedComponents.length;
  }

  getFilteredModules(): RoleModule[] {
    // * Handle async loading: return empty array if modules not loaded yet
    if (!this.roleModules) {
      return [];
    }

    let filteredModules = [...this.roleModules];

    // ! apply "show selected only" Filter first
    if (this.showSelectedOnly) {
      filteredModules = filteredModules.filter((module) => {
        const rootSelected = module.root_component?.selected;

        const childSelected = module.components?.some(
          (component) => component.selected,
        );

        return rootSelected || childSelected;
      });
    }

    // ! apply Search Filter (global or local)
    if (this.activeSearchString && this.activeSearchString.trim()) {
      const search = this.activeSearchString.toLowerCase().trim();

      filteredModules = filteredModules.filter((module) => {
        const rootMatches = module.name?.toLowerCase().includes(search);

        const childMatches = module.components?.some((component) =>
          component.name?.toLowerCase().includes(search),
        );

        return rootMatches || childMatches;
      });
    }

    return filteredModules;
  }

  onShowSelectedOnlyChange(checked: boolean): void {
    this.showSelectedOnly = checked;
  }

  getFilteredChildComponents(components: RoleComponent[]): RoleComponent[] {
    if (!components) return [];

    let filtered = [...components];

    // * apply Search Filter (global or local)
    if (this.activeSearchString && this.activeSearchString.trim()) {
      const search = this.activeSearchString.toLowerCase().trim();
      filtered = filtered.filter((component) =>
        component.name?.toLowerCase().includes(search),
      );
    }

    // * apply "show selected only" Filter
    if (this.showSelectedOnly) {
      filtered = filtered.filter((component) => component.selected);
    }

    return filtered;
  }

  shouldShowRootComponent(rootComponent: RoleComponent): boolean {
    if (!rootComponent) return false;

    // * apply Search Filter (global or local)
    if (this.activeSearchString && this.activeSearchString.trim()) {
      const search = this.activeSearchString.toLowerCase().trim();
      const matchesSearch = rootComponent.name?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }

    // * apply "show selected only" Filter
    if (this.showSelectedOnly && !rootComponent.selected) {
      return false;
    }

    return true;
  }

  private addToSelectedComponents(
    component: RoleComponent,
    isRoot: boolean = false,
  ): void {
    if (!this.selectedComponents.find((c) => c.id === component.id)) {
      if (isRoot) {
        component.is_root_component = true;
      }
      this.selectedComponents.push(component);
    }
  }

  private removeFromSelectedComponents(component: RoleComponent): void {
    this.selectedComponents = this.selectedComponents.filter(
      (c) => c.id !== component.id,
    );
  }

  /**
   * Returns count of components/modules matching GLOBAL search only
   * Used by parent form to determine if this tab has search matches
   * Local search does NOT report to parent (tab-specific only)
   */
  getSearchStringMatchesCount(): number {
    // Only report matches for GLOBAL search, not local
    if (!this.globalSearchString || !this.globalSearchString.trim()) {
      return 0;
    }

    const search = this.globalSearchString.toLowerCase().trim();
    let matchCount = 0;

    this.roleModules?.forEach((module) => {
      // * Check root component
      if (module.root_component?.name?.toLowerCase().includes(search)) {
        matchCount++;
      }

      // * Check child components
      module.components?.forEach((component) => {
        if (component.name?.toLowerCase().includes(search)) {
          matchCount++;
        }
      });
    });

    return matchCount;
  }

  setPermissionsHeight(): void {
    // TODO: setting the height this way is an anti-pattern but all issues around "dynamic forms"
    // TODO: are causing this dirty hack that is necessary and probably the most optimal solution for now
    // TODO: remove once all "dynamic forms" are refactored and deleted!
    const container: HTMLElement = document.querySelector('.form-tab-content');
    const containerHeight: number = container?.clientHeight;
    const scrollableContainer: HTMLElement =
      document.querySelector('.role-features');

    const rootFontSize = parseFloat(
      getComputedStyle(document.documentElement).fontSize,
    );

    const heightInRem = (containerHeight - 49) / rootFontSize;

    if (containerHeight && scrollableContainer) {
      scrollableContainer.style.height = `${heightInRem}rem`;
    }
  }
}
